import { Box, Chip, Radio, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';

import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import { formatQty, shortfallAgainstFill } from '../utils/bulkLotNdcStatus';
import { warningTextTone } from '../utils/warningTone';

/** A lot inside this many days of expiry is flagged, without being withheld. */
const EXPIRING_SOON_DAYS = 30;

// Radio · Lot · Expires · Available (free in this batch, POM-6536). The lot and the free figure
// take fixed widths, so Expires starts right after the lot on one line and every row lines up.
const LOT_TABLE_COLUMNS = '36px 200px minmax(0, 1fr) 220px';

/**
 * "90 set aside for 3 rows ahead", under the stock of a lot that carries the batch's share, or
 * null. Set aside, not used: nothing leaves the stock until a fill, and these are the rows that
 * fill from the lot before this one (batchShareForRow), so "used" read as stock already taken. Its
 * own line, as it does not fit beside the stock in the column.
 */
const describeSetAside = (lot) => {
  const rowCount = Number(lot.batchUsedRowCount);
  if (!rowCount) return null;

  return `${formatQty(lot.batchUsedQty)} set aside for ${rowCount} row${
    rowCount === 1 ? '' : 's'
  } ahead`;
};

const LOT_TABLE_HEADER_SX = {
  display: 'grid',
  gridTemplateColumns: LOT_TABLE_COLUMNS,
  columnGap: 1,
  px: 1,
  py: 0.75,
  bgcolor: 'grey.100',
  borderBottom: '1px solid',
  borderColor: 'divider',
  '& > *': {
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'text.secondary',
  },
};

/**
 * POM-6010 — the stocked lots for the selected NDC, as a table under the Lot heading.
 *
 * The parent decides whether this list is on offer at all: a fill is either allocated from stock or
 * hand-recorded, never both, so in manual mode it is not rendered.
 */
const ChangeNdcLotStockLots = ({
  lots,
  selectedNdc,
  selectedLotId,
  recordedLotIds,
  fillQuantity,
  shortfallAllowed,
  facilityName,
  onSelectLot,
}) => {
  if (!selectedNdc) {
    return (
      <Typography variant='body2' color='text.secondary'>
        Select an NDC to see its lots.
      </Typography>
    );
  }

  if (lots.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary'>
        {/* POM-6121 - what empty MEANS depends on the switch. With the claim available the list
            holds every in-date lot with any stock, so empty is no stock at all; without it a lot
            that cannot cover the fill is still withheld, and saying "no stock" would send the
            operator hunting for units sitting on the shelf. */}
        {shortfallAllowed
          ? `No lot in stock at ${facilityName ?? 'this facility'}.`
          : `No lot in stock can cover the full quantity of ${formatQty(fillQuantity)} at ${
              facilityName ?? 'this facility'
            }.`}
      </Typography>
    );
  }

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      <Box sx={LOT_TABLE_HEADER_SX}>
        <span />
        <span>Lot</span>
        <span>Expires</span>
        <Box component='span' sx={{ textAlign: 'right' }}>
          {lots.some((lot) => lot.stockQty != null) ? 'Free in this batch' : 'Available'}
        </Box>
      </Box>
      {lots.map((lot) => {
        const isSelected = lot.inventoryLotId === selectedLotId;
        const expiringSoon =
          Number(lot.daysUntilExpiry) <= EXPIRING_SOON_DAYS && Number(lot.daysUntilExpiry) >= 0;
        const shortfall = shortfallAgainstFill({ lot, fillQuantity });

        return (
          /* A real label around a real radio, all sharing one name, so the list is a native radio
             group: Tab reaches it, the arrow keys move through it, and Space selects. */
          <Box
            key={lot.inventoryLotId}
            component='label'
            sx={{
              display: 'grid',
              gridTemplateColumns: LOT_TABLE_COLUMNS,
              columnGap: 1,
              alignItems: 'center',
              px: 1,
              py: 0.5,
              cursor: 'pointer',
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:last-of-type': { borderBottom: 0 },
              // Green, unlike the navy NDC cards, so the picked lot reads as a different choice.
              bgcolor: (theme) =>
                isSelected ? alpha(theme.palette.success.main, 0.08) : undefined,
              boxShadow: (theme) =>
                isSelected ? `inset 3px 0 0 ${theme.palette.success.main}` : 'none',
              '&:focus-within': { outline: '2px solid', outlineColor: 'primary.main' },
            }}
          >
            <Radio
              checked={isSelected}
              onChange={() => onSelectLot(lot.inventoryLotId)}
              name='stocked-lot'
              size='small'
              color='success'
              inputProps={{ 'aria-label': lot.lotNumber }}
            />
            <Stack direction='row' spacing={0.75} alignItems='center' sx={{ minWidth: 0 }}>
              <Typography variant='body2'>{lot.lotNumber}</Typography>
              {/* Marked on every recorded lot, not only the seeded one, so a multi-lot fill still
                  shows which lots it is currently on. */}
              {recordedLotIds.has(lot.inventoryLotId) && (
                <Chip label='CURRENT' size='small' variant='outlined' color='primary' />
              )}
            </Stack>
            <Typography variant='caption' color='text.secondary'>
              {String(lot.expirationDate).slice(0, 10)}
              {expiringSoon ? ` · Expiring · ${lot.daysUntilExpiry}d` : ''}
            </Typography>
            <Box sx={{ textAlign: 'right', minWidth: 0 }}>
              {lot.stockQty != null ? (
                <>
                  <Typography variant='body2'>{`${formatQty(lot.remainingQty)} free`}</Typography>
                  <Typography variant='caption' component='div' color='text.secondary'>
                    {`${formatQty(lot.stockQty)} in stock`}
                  </Typography>
                  {describeSetAside(lot) && (
                    <Typography variant='caption' component='div' color='text.secondary'>
                      {describeSetAside(lot)}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant='body2'>{formatQty(lot.remainingQty)}</Typography>
              )}
              {/* POM-6121 — said on the row, not only after it is picked: with two short lots on
                  offer the operator compares them before choosing. */}
              {shortfall > 0 && (
                <Typography variant='caption' component='div' sx={{ color: warningTextTone }}>
                  {`${formatQty(shortfall)} ${SYSTEM_DEFICIT.toLowerCase()}`}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

ChangeNdcLotStockLots.propTypes = {
  /** The selected NDC's usable lots, already ordered earliest expiry first. */
  lots: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedNdc: PropTypes.string,
  selectedLotId: PropTypes.string,
  /** The lots the row records today, for the CURRENT marker. */
  recordedLotIds: PropTypes.instanceOf(Set).isRequired,
  fillQuantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** True when a lot that cannot cover the fill is offered with its shortfall claimed. */
  shortfallAllowed: PropTypes.bool,
  facilityName: PropTypes.string,
  onSelectLot: PropTypes.func.isRequired,
};

ChangeNdcLotStockLots.defaultProps = {
  selectedNdc: null,
  selectedLotId: null,
  fillQuantity: '',
  shortfallAllowed: false,
  facilityName: null,
};

export default ChangeNdcLotStockLots;
